import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface WebSocketMessage {
  type: string;
  data: any;
  timestamp: string;
  userId?: string;
}

export interface WebSocketStatus {
  connected: boolean;
  connecting: boolean;
  error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3; // Reduced for ephemeral connections
  private reconnectDelay = 1000; // Start with 1 second
  private reconnectTimer: any = null;
  private pingInterval: any = null;
  private pongTimeout: any = null;
  private userId: string | null = null;
  private connectionExpiryTimer: any = null;

  // Behavior subjects for reactive state management
  private _status = new BehaviorSubject<WebSocketStatus>({
    connected: false,
    connecting: false,
    error: null
  });

  private _messages = new BehaviorSubject<WebSocketMessage[]>([]);

  // Public observables
  public status$ = this._status.asObservable();
  public messages$ = this._messages.asObservable();

  constructor() {
    // No auto-connect on page visibility change
    // WebSocket should only connect when explicitly called after authentication
  }

  /**
   * Connect to WebSocket server
   */
  public connect(userId?: string, authToken?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this._status.value.connected || this._status.value.connecting) {
        resolve();
        return;
      }

      this._status.next({
        connected: false,
        connecting: true,
        error: null
      });

      try {
        // Use environment variable or fallback to hardcoded endpoint
        let wsEndpoint = environment.websocketEndpoint || 
          'wss://u34cg1p994.execute-api.us-east-1.amazonaws.com/prod';
        
        // Add authentication parameters to URL if provided
        if (authToken) {
          const apiKey = 'hr6lnjZ5tj36chKnO3cyR5CnMI0Oio5I61lZUfrp'; // Use the correct API key value
          wsEndpoint += `?token=${encodeURIComponent(authToken)}&apiKey=${encodeURIComponent(apiKey)}`;
          
        }
        
        // Create WebSocket connection
        this.socket = new WebSocket(wsEndpoint);

        this.socket.onopen = () => {
          
          this._status.next({
            connected: true,
            connecting: false,
            error: null
          });
          
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          
          // Start ping interval
          this.startPingInterval();
          
          // Set connection expiry timer (1 minute)
          this.setConnectionExpiryTimer();
          
          // Register user with authentication if both userId and authToken provided
          if (userId && authToken) {
            this.userId = userId;
            this.registerUserWithAuth(userId, authToken);
          } else if (userId) {
            this.userId = userId;
            this.registerUser(userId);
          }
          
          resolve();
        };

        this.socket.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            
            // Add message to history
            const currentMessages = this._messages.value;
            this._messages.next(currentMessages.concat([message]));
            
            // Handle different message types
            this.handleMessage(message);
          } catch (error) {
            
          }
        };

        this.socket.onclose = (event) => {
          
          this._status.next({
            connected: false,
            connecting: false,
            error: null
          });
          
          this.stopPingInterval();
          this.cleanupPongTimeout();
          this.clearConnectionExpiryTimer();
          
          // No intentar reconectar después de 3 intentos fallidos
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            
            this._status.next({
              connected: false,
              connecting: false,
              error: 'Conexión WebSocket no disponible. El estado del CV se mostrará cuando esté disponible.'
            });
          }
        };

        this.socket.onerror = (error) => {
          
          this._status.next({
            connected: false,
            connecting: false,
            error: 'Connection error occurred'
          });
          reject(new Error('WebSocket connection failed'));
        };

      } catch (error) {
        
        this._status.next({
          connected: false,
          connecting: false,
          error: 'Failed to create connection'
        });
        reject(error);
      }
    });
  }

  /**
   * Set connection expiry timer (5 minutes for CV processing)
   */
  private setConnectionExpiryTimer(): void {
    this.clearConnectionExpiryTimer();
    
    this.connectionExpiryTimer = setTimeout(() => {
      
      this.disconnect();
      
      // Notify frontend about expiry
      this._status.next({
        connected: false,
        connecting: false,
        error: 'Connection expired - maximum 5 minutes allowed'
      });
    }, 300000); // 5 minutes for CV processing
  }

  /**
   * Clear connection expiry timer
   */
  private clearConnectionExpiryTimer(): void {
    if (this.connectionExpiryTimer) {
      clearTimeout(this.connectionExpiryTimer);
      this.connectionExpiryTimer = null;
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  public disconnect(): void {
    if (this.socket) {
      // Don't send close code 1000 (user requested) unless actually requested by user
      this.socket.close(1001, 'Going away');
      this.socket = null;
    }
    
    this.stopPingInterval();
    this.cleanupPongTimeout();
    this.clearConnectionExpiryTimer();
    
    this._status.next({
      connected: false,
      connecting: false,
      error: null
    });
  }

  /**
   * Register user for notifications
   */
  public registerUser(userId: string): void {
    if (this._status.value.connected && this.socket) {
      this.userId = userId;
      const message = {
        action: 'register_user',
        userId: userId
      };
      
      this.socket.send(JSON.stringify(message));
      
    } else {
      
    }
  }

  /**
   * Register user with authentication token
   */
  private registerUserWithAuth(userId: string, authToken: string): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const authMessage = {
        action: 'register_user',
        userId: userId,
        authToken: authToken,
        timestamp: new Date().toISOString()
      };
      
      
      this.socket.send(JSON.stringify(authMessage));
    } else {
      
    }
  }

  /**
   * Register user without authentication (fallback)
   */
  /**
   * Send custom message
   */
  public sendMessage(message: any): void {
    if (this._status.value.connected && this.socket) {
      this.socket.send(JSON.stringify(message));
    } else {
      
    }
  }

  /**
   * Get current connection status
   */
  public getStatus(): WebSocketStatus {
    return this._status.value;
  }

  /**
   * Get current messages
   */
  public getMessages(): WebSocketMessage[] {
    return this._messages.value;
  }

  /**
   * Clear message history
   */
  public clearMessages(): void {
    this._messages.next([]);
  }

  /**
   * Check if connected
   */
  public isConnected(): boolean {
    return this._status.value.connected;
  }

  /**
   * Handle incoming messages
   */
  private handleMessage(message: WebSocketMessage): void {
    
    switch (message.type) {
      case 'user_registered':
        
        this._messages.next(this._messages.value.concat([message]));
        break;
        
      case 'cv_status_update':
        
        this._messages.next(this._messages.value.concat([message]));
        break;
        
      case 'cv_analysis_update':
        
        this._messages.next(this._messages.value.concat([message]));
        break;
        
      case 'cv_analysis_complete':
        
        this._messages.next(this._messages.value.concat([message]));
        break;
        
      case 'cv_analysis_failed':
        
        this._messages.next(this._messages.value.concat([message]));
        break;
        
      case 'pong':
        
        this.handlePong();
        break;
        
      case 'connection_expired':
        
        this.disconnect();
        break;
        
      default:
        
        // Still emit unknown messages so components can handle them if needed
        this._messages.next(this._messages.value.concat([message]));
    }
  }

  /**
   * Start ping interval to keep connection alive
   */
  private startPingInterval(): void {
    this.pingInterval = setInterval(() => {
      if (this._status.value.connected && this.socket) {
        this.sendMessage({ action: 'ping' });
        
        // Set pong timeout
        this.pongTimeout = setTimeout(() => {
          
          this.disconnect();
          // Don't auto-reconnect for expired connections
        }, 10000); // 10 second timeout
      }
    }, 30000); // Send ping every 30 seconds
  }

  /**
   * Stop ping interval
   */
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Stop ping/pong completely (public method)
   */
  public stopPingPong(): void {
    
    this.stopPingInterval();
    this.cleanupPongTimeout();
  }

  /**
   * Handle pong response
   */
  private handlePong(): void {
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  /**
   * Cleanup pong timeout
   */
  private cleanupPongTimeout(): void {
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    
    this.reconnectAttempts++;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 10000); // Max 10 seconds for ephemeral connections
    
    
    this.reconnectTimer = setTimeout(() => {
      this.connect(this.userId || undefined);
    }, this.reconnectDelay);
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.disconnect();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
  }
}
